import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  KeyRound,
  Lock,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Search,
  Sun,
  User,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AccountingMode } from '@bruhat/shared';
import { useAccountingMode } from '@/hooks/use-accounting-mode';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth.store';
import { useUiStore, type Theme } from '@/store/ui.store';
import { formatDate, initials } from '@/lib/format';
import { Badge } from '@/components/ui';
import { ChangePasswordModal } from './ChangePassword';

export function Topbar() {
  const profile = useAuthStore((s) => s.profile);
  const companyId = useAuthStore((s) => s.companyId);
  const financialYear = useAuthStore((s) => s.financialYear);
  const financialYears = useAuthStore((s) => s.financialYears);
  const switchCompany = useAuthStore((s) => s.switchCompany);
  const setFinancialYear = useAuthStore((s) => s.setFinancialYear);
  const logout = useAuthStore((s) => s.logout);

  const setMobileOpen = useUiStore((s) => s.setSidebarMobileOpen);
  const setPaletteOpen = useUiStore((s) => s.setCommandPaletteOpen);

  const navigate = useNavigate();
  const { mode, canSwitch, setMode } = useAccountingMode();

  const [menu, setMenu] = useState<'company' | 'year' | 'user' | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const company = profile?.companies.find((c) => c.id === companyId);

  const close = () => setMenu(null);

  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b border-surface-border bg-white/90 px-3 backdrop-blur dark:border-night-border dark:bg-night-raised/90">
      <button
        onClick={() => setMobileOpen(true)}
        aria-label="Open navigation"
        className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken lg:hidden dark:hover:bg-night-sunken"
      >
        <Menu className="h-4 w-4" />
      </button>

      {/* Command bar: the fastest way to reach any record. */}
      <button
        onClick={() => setPaletteOpen(true)}
        className="flex h-9 flex-1 items-center gap-2 rounded-lg border border-surface-border bg-surface-muted px-3 text-sm text-ink-subtle transition-colors hover:border-brand-300 hover:bg-white sm:max-w-md dark:border-night-border dark:bg-night-sunken dark:hover:border-brand-700"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="truncate">Search units, customers, vouchers…</span>
        <kbd className="kbd ml-auto hidden sm:inline-flex">⌘K</kbd>
      </button>

      <div className="ml-auto flex items-center gap-1">
        {/*
          Simple / Accountant. The toggle changes vocabulary and where you land,
          never what you are allowed to see — every endpoint checks permissions
          on its own. Roles whose work never needs debits are not offered it.
        */}
        {canSwitch && (
          <div className="mr-1 hidden rounded-lg border border-surface-border p-0.5 sm:flex dark:border-night-border">
            <button
              onClick={() => {
                setMode(AccountingMode.SIMPLE);
                navigate('/simple');
              }}
              className={cn(
                'rounded-md px-2.5 py-1 text-2xs font-medium transition',
                mode === AccountingMode.SIMPLE
                  ? 'bg-brand-500 text-white'
                  : 'text-ink-muted hover:text-ink dark:text-slate-400',
              )}
            >
              Simple
            </button>
            <button
              onClick={() => {
                setMode(AccountingMode.ACCOUNTANT);
                navigate('/dashboard');
              }}
              className={cn(
                'rounded-md px-2.5 py-1 text-2xs font-medium transition',
                mode === AccountingMode.ACCOUNTANT
                  ? 'bg-brand-500 text-white'
                  : 'text-ink-muted hover:text-ink dark:text-slate-400',
              )}
            >
              Accountant
            </button>
          </div>
        )}
        {/* Company switcher */}
        <div className="relative">
          <button
            onClick={() => setMenu(menu === 'company' ? null : 'company')}
            className="flex h-9 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-ink hover:bg-surface-sunken dark:text-slate-100 dark:hover:bg-night-sunken"
          >
            <Building2 className="h-4 w-4 text-ink-muted" />
            <span className="hidden max-w-[140px] truncate md:inline">
              {company?.name ?? 'Select company'}
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-ink-subtle" />
          </button>

          {menu === 'company' && (
            <Dropdown onClose={close}>
              <p className="px-3 py-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-subtle">
                Companies
              </p>
              {profile?.companies.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    void switchCompany(item.id);
                    close();
                  }}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-surface-muted dark:hover:bg-night-sunken"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-ink dark:text-slate-100">
                      {item.name}
                    </span>
                    <span className="block truncate text-2xs text-ink-subtle">{item.roleLabel}</span>
                  </span>
                  {item.id === companyId && <Check className="h-4 w-4 shrink-0 text-brand-500" />}
                </button>
              ))}
            </Dropdown>
          )}
        </div>

        {/* Financial year selector */}
        {financialYear && (
          <div className="relative">
            <button
              onClick={() => setMenu(menu === 'year' ? null : 'year')}
              className="flex h-9 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-ink hover:bg-surface-sunken dark:text-slate-100 dark:hover:bg-night-sunken"
            >
              <CalendarDays className="h-4 w-4 text-ink-muted" />
              <span className="tabular hidden sm:inline">{financialYear.name}</span>
              {financialYear.isLocked && <Lock className="h-3 w-3 text-warning" />}
              <ChevronDown className="h-3.5 w-3.5 text-ink-subtle" />
            </button>

            {menu === 'year' && (
              <Dropdown onClose={close}>
                <p className="px-3 py-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-subtle">
                  Financial year
                </p>
                {financialYears.map((year) => (
                  <button
                    key={year.id}
                    onClick={() => {
                      setFinancialYear(year.id);
                      close();
                    }}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-surface-muted dark:hover:bg-night-sunken"
                  >
                    <span>
                      <span className="tabular block font-medium text-ink dark:text-slate-100">
                        {year.name}
                      </span>
                      <span className="block text-2xs text-ink-subtle">
                        {formatDate(year.startDate)} – {formatDate(year.endDate)}
                      </span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      {year.isLocked && <Badge tone="warning">Locked</Badge>}
                      {year.id === financialYear.id && <Check className="h-4 w-4 text-brand-500" />}
                    </span>
                  </button>
                ))}
              </Dropdown>
            )}
          </div>
        )}

        <ThemeToggle />

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setMenu(menu === 'user' ? null : 'user')}
            aria-label="Account"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500 text-xs font-semibold text-white"
          >
            {profile ? initials(profile.fullName) : <User className="h-4 w-4" />}
          </button>

          {menu === 'user' && (
            <Dropdown onClose={close}>
              <div className="border-b border-surface-border px-3 py-2 dark:border-night-border">
                <p className="truncate text-sm font-medium text-ink dark:text-slate-100">
                  {profile?.fullName}
                </p>
                <p className="truncate text-2xs text-ink-subtle">{profile?.email}</p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {profile?.roles.map((role) => (
                    <Badge key={role} tone="brand">
                      {role.replace(/_/g, ' ').toLowerCase()}
                    </Badge>
                  ))}
                  {profile?.customRoles?.map((name) => (
                    <Badge key={name} tone="brand">
                      {name}
                    </Badge>
                  ))}
                </div>
              </div>
              <Link
                to="/settings"
                onClick={close}
                className="block px-3 py-2 text-sm text-ink hover:bg-surface-muted dark:text-slate-100 dark:hover:bg-night-sunken"
              >
                Settings
              </Link>
              <button
                onClick={() => {
                  setChangingPassword(true);
                  close();
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface-muted dark:text-slate-100 dark:hover:bg-night-sunken"
              >
                <KeyRound className="h-4 w-4" />
                Change password
              </button>
              <button
                onClick={() => {
                  void logout();
                  close();
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-negative hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </Dropdown>
          )}
        </div>
      </div>
      {changingPassword && <ChangePasswordModal open onClose={() => setChangingPassword(false)} />}
    </header>
  );
}

function Dropdown({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} aria-hidden />
      <div className="absolute right-0 top-full z-20 mt-1 w-64 overflow-hidden rounded-xl border border-surface-border bg-white py-1 shadow-raised dark:border-night-border dark:bg-night-raised">
        {children}
      </div>
    </>
  );
}

const THEMES: Array<{ value: Theme; icon: typeof Sun; label: string }> = [
  { value: 'light', icon: Sun, label: 'Light' },
  { value: 'dark', icon: Moon, label: 'Dark' },
  { value: 'system', icon: Monitor, label: 'System' },
];

function ThemeToggle() {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);

  const current = THEMES.findIndex((t) => t.value === theme);
  const next = THEMES[(current + 1) % THEMES.length];
  const Icon = THEMES[current === -1 ? 2 : current].icon;

  return (
    <button
      onClick={() => setTheme(next.value)}
      title={`Theme: ${THEMES[current === -1 ? 2 : current].label}. Click for ${next.label}.`}
      aria-label="Toggle theme"
      className={cn(
        'flex h-9 w-9 items-center justify-center rounded-lg text-ink-muted',
        'hover:bg-surface-sunken hover:text-ink dark:hover:bg-night-sunken dark:hover:text-slate-100',
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
